import React, { useMemo } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { SnackbarProvider } from "notistack";
import { registerSW } from "virtual:pwa-register";

import { WidgetProvider, useWidget } from "/imports/state/WidgetContext";
import { AuthProvider } from "/imports/state/AuthContext";
import App from "/imports/ui/App";
import { createWidgetTheme } from "/imports/ui/styles/theme";
import { initAnalytics } from "/imports/libs/analytics";
import { captureInstallPrompt } from "/imports/libs/push";

import "./main.css";

function ThemedApp() {
  const { widget } = useWidget();
  const theme = useMemo(
    () => createWidgetTheme({ primaryColor: widget?.color || undefined, secondaryColor: widget?.secondaryColor || undefined }),
    [widget?.color, widget?.secondaryColor]
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: "bottom", horizontal: "center" }} autoHideDuration={3000} disableWindowBlurListener>
        <AuthProvider>
          <App />
        </AuthProvider>
      </SnackbarProvider>
    </ThemeProvider>
  );
}

initAnalytics();
// Chrome fires its install offer once, early: caught here for the install card.
captureInstallPrompt();

// Silent auto-update: a new deploy's worker takes over on the next open. A no-op
// under `vite dev` (the worker only exists in a build).
registerSW({ immediate: true });

const container = document.getElementById("react-target");
const root = createRoot(container);

root.render(
  <BrowserRouter>
    <WidgetProvider>
      <ThemedApp />
    </WidgetProvider>
  </BrowserRouter>
);
